import { autoGravity } from "@cloudinary/url-gen/qualifiers/gravity";
import { limitFill } from "@cloudinary/url-gen/actions/resize";
import { Heading, Text } from "@ttc3k/trekker";
import {
  EnumProductType,
  EnumPurchasedProductCustomFieldResponsesType,
  OrderByIdQuery,
} from "gql/generated";
import { useTranslation } from "react-i18next";
import {
  ProductTypeConfig,
  getProductTypeConfig,
} from "shared/config/productTypeConfig";
import { getMultilingualString } from "shared/utils";
import { CloudinaryImage, SmartImage } from "shared/components/atoms";
import { getVariantImage } from "shared/merchCatalog";
import { css } from "styled-system/css";
import { Box, Flex } from "styled-system/jsx";
import { isLandscape } from "shared/utils/isLandscape";
import { useMediaQuery } from "react-responsive";
import { differenceInCalendarDays, format, parse } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { parseSessionId } from "@shared/events";
import { ReservationSection } from "./ReservationSection";

type PurchasedProductProps = {
  purchasedProduct: NonNullable<
    NonNullable<OrderByIdQuery["orderById"]>["purchasedProducts"][0]
  >;
  guestCount: number;
  tierVariant?: string | null;
};

/**
 * Resolve the selling-config tier label chosen at checkout (fees / renewable
 * fees). Returns an empty string when the purchase has no tier.
 */
const getPurchasedProductTierName = (
  purchasedProduct: PurchasedProductProps["purchasedProduct"],
): string => {
  if (!purchasedProduct.tierId) return "";
  const option = purchasedProduct.product.sellingConfig?.options?.find(
    o => o?._id === purchasedProduct.tierId,
  );
  return option?.name ? getMultilingualString(option.name) : "";
};

const MERCH_THUMBNAIL_SIZE = 64;

/** "Colour: Red · Size: M", falling back to the variant's stored label. */
const getMerchVariantDescription = (
  purchasedProduct: PurchasedProductProps["purchasedProduct"],
): string => {
  const metadata = purchasedProduct.product.merchMetadata;
  const variant = metadata?.variants?.find(
    v => v?._id === purchasedProduct.merchVariantId,
  );
  if (!variant) return "";
  const parts = (metadata?.dimensions ?? []).flatMap(dim => {
    const value = dim?.values?.find(v =>
      variant.dimensionValueIds?.includes(v?._id),
    );
    if (!dim || !value?.label) return [];
    const valueLabel = getMultilingualString(value.label);
    const dimName = dim.name ? getMultilingualString(dim.name) : "";
    return valueLabel ? [dimName ? `${dimName}: ${valueLabel}` : valueLabel] : [];
  });
  return parts.length > 0 ? parts.join(" · ") : (variant.label ?? "");
};

const MerchDetails = ({ purchasedProduct }: PurchasedProductProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.PURCHASE_DETAILS",
  });
  const name = getMultilingualString(purchasedProduct.product.name);
  const variantDescription = getMerchVariantDescription(purchasedProduct);
  const image = getVariantImage(
    purchasedProduct.product,
    purchasedProduct.merchVariantId,
  );

  return (
    <Flex
      alignItems="center"
      gap="300"
      w="full"
      p="300"
      border="2px solid"
      borderColor="border.dark"
      borderRadius="100"
    >
      {image && (
        <SmartImage
          image={image}
          width={MERCH_THUMBNAIL_SIZE}
          height={MERCH_THUMBNAIL_SIZE}
          alt={name}
        />
      )}
      <Flex flexDir="column" gap="75" minW="0" flex="1">
        <Heading size="h5">{name}</Heading>
        {variantDescription && (
          <Text visual="bodyMedium" color="text.mid">
            {variantDescription}
          </Text>
        )}
        <Text visual="bodyMedium" color="text.light">
          {t("QUANTITY", { quantity: purchasedProduct.quantity })}
        </Text>
      </Flex>
    </Flex>
  );
};

const AccommodationDetails = ({
  purchasedProduct,
  guestCount,
}: PurchasedProductProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.PURCHASE_DETAILS",
  });
  const isMedium = useMediaQuery({ query: "(min-width: 768px)" });

  const startDate =
    purchasedProduct.inventoryClaims?.[0]?.claimedDateRangeISO?.start ??
    new Date();
  const endDate =
    purchasedProduct.inventoryClaims?.[0]?.claimedDateRangeISO?.end ??
    new Date();
  const nights = differenceInCalendarDays(endDate, startDate);

  return (
    <Flex
      alignItems="center"
      justifyContent="stretch"
      gap="0"
      w="full"
      border="2px solid"
      borderColor="border.dark"
      borderRadius="100"
    >
      <Flex flexDir="column" gap="75" w="full" p="300">
        <Heading size="h5" lineClamp={1}>
          {purchasedProduct.product.name.en}
        </Heading>
        <Text visual="bodyMedium" color="text.light">
          {t("NIGHTS", { nights })} • {t("GUESTS", { guests: guestCount })}
        </Text>
      </Flex>
      <CloudinaryImage
        image={purchasedProduct.product.images?.[0]}
        imageStyleProps={{
          className: css({
            width: {
              base: "140px",
              md: "200px",
            },
            height: {
              base: "126px",
              md: "180px",
            },
            objectFit: "cover",
            aspectRatio: "unset",
            borderTopRightRadius: "75",
            borderBottomRightRadius: "75",
          }),
        }}
        alt={purchasedProduct.product.name.en ?? ""}
        resize={
          isLandscape(purchasedProduct.product.images?.[0])
            ? limitFill()
                .height(isMedium ? 180 : 126)
                .gravity(autoGravity())
            : limitFill()
                .width(isMedium ? 200 : 140)
                .gravity(autoGravity())
        }
      />
    </Flex>
  );
};

const QuantityDetails = ({
  purchasedProduct,
  tierVariant,
}: PurchasedProductProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.PURCHASE_DETAILS",
  });

  const tierName = getPurchasedProductTierName(purchasedProduct);

  return (
    <Flex
      flexDir="column"
      gap="75"
      w="full"
      p="300"
      border="2px solid"
      borderColor="border.dark"
      borderRadius="100"
    >
      <Heading size="h5">{purchasedProduct.product.name.en}</Heading>
      {tierName && (
        <Text visual="bodyMedium" color="text.light">
          {tierVariant
            ? t("TIER_WITH_VARIANT", { tier: tierName, variant: tierVariant })
            : t("TIER", { tier: tierName })}
        </Text>
      )}
      <Text visual="bodyMedium" color="text.light">
        {t("QUANTITY", { quantity: purchasedProduct.quantity })}
      </Text>
      <CustomFieldAnswers purchasedProduct={purchasedProduct} />
    </Flex>
  );
};

const CustomFieldAnswers = ({
  purchasedProduct,
}: Pick<PurchasedProductProps, "purchasedProduct">) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.PURCHASE_DETAILS",
  });
  const answers = (purchasedProduct.customFieldResponses ?? []).flatMap(
    answer => {
      if (!answer) return [];
      const values = (answer.response ?? []).filter(Boolean);
      if (values.length === 0) return [];
      const isYesNo =
        answer.type === EnumPurchasedProductCustomFieldResponsesType.Boolean &&
        !answer.isChoiceList;
      const value = isYesNo
        ? t(values[0] === "true" ? "ANSWER_YES" : "ANSWER_NO")
        : values.join(", ");
      return [{ fieldId: answer.fieldId, label: answer.label, value }];
    },
  );
  if (answers.length === 0) return null;

  return (
    <Flex flexDir="column" gap="50" pt="100">
      {answers.map(answer => (
        <Text
          key={answer.fieldId}
          visual="bodyMedium"
          color="text.mid"
          whiteSpace="pre-wrap"
        >
          <strong>{answer.label}:</strong> {answer.value}
        </Text>
      ))}
    </Flex>
  );
};

const toDate = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatSessionDates = (sessionIds: string[]): string => {
  const dates = [
    ...new Set(sessionIds.map(id => parseSessionId(id).date)),
  ].sort();
  if (dates.length === 0) return "";

  // Group into contiguous runs.
  const runs: string[][] = [[dates[0]]];
  for (let i = 1; i < dates.length; i++) {
    const prev = toDate(dates[i - 1]);
    const curr = toDate(dates[i]);
    const diffMs = curr.getTime() - prev.getTime();
    if (diffMs <= 86_400_000) {
      runs[runs.length - 1].push(dates[i]);
    } else {
      runs.push([dates[i]]);
    }
  }

  return runs
    .map(run => {
      const first = toDate(run[0]);
      if (run.length === 1) return format(first, "MMM d, yyyy");
      const last = toDate(run[run.length - 1]);
      if (first.getFullYear() === last.getFullYear() && first.getMonth() === last.getMonth()) {
        return `${format(first, "MMM d")}–${format(last, "d, yyyy")}`;
      }
      if (first.getFullYear() === last.getFullYear()) {
        return `${format(first, "MMM d")}–${format(last, "MMM d, yyyy")}`;
      }
      return `${format(first, "MMM d, yyyy")}–${format(last, "MMM d, yyyy")}`;
    })
    .join(", ");
};

const EventDetails = ({ purchasedProduct }: PurchasedProductProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.PURCHASE_DETAILS",
  });

  const productTiers = purchasedProduct.product.productTiers ?? [];
  const guestsPerTier = (purchasedProduct.guestsPerTier ?? []).filter(
    (g): g is NonNullable<typeof g> => Boolean(g && g.count > 0),
  );
  const allSessionIds = (purchasedProduct.sessionIds ?? []).filter(
    (id): id is string => Boolean(id),
  );

  const hasTiers = guestsPerTier.length > 0 && productTiers.length > 0;

  const tierLines = hasTiers
    ? guestsPerTier.map(gt => {
        const tier = productTiers.find(pt => String(pt?._id) === String(gt.tierId));
        const name = tier?.name
          ? getMultilingualString(tier.name)
          : String(gt.tierId);
        return { name, count: gt.count, sessionIds: gt.sessionIds };
      })
    : [];

  const sessionLabel =
    allSessionIds.length > 0
      ? formatSessionDates(allSessionIds)
      : t("EVENT_WHOLE_EVENT");

  return (
    <Flex
      flexDir="column"
      gap="75"
      w="full"
      p="300"
      border="2px solid"
      borderColor="border.dark"
      borderRadius="100"
    >
      <Heading size="h5">{purchasedProduct.product.name.en}</Heading>

      {hasTiers ? (
        tierLines.map((line, i) => (
          <Text key={i} visual="bodyMedium" color="text.light">
            {t("EVENT_TIER_QTY", { tier: line.name, count: line.count })}
          </Text>
        ))
      ) : (
        <Text visual="bodyMedium" color="text.light">
          {t("QUANTITY", { quantity: purchasedProduct.quantity })}
        </Text>
      )}

      <Text visual="bodyMedium" color="text.light">
        {t("EVENT_SESSION", { count: allSessionIds.length, dates: sessionLabel })}
      </Text>
    </Flex>
  );
};

type CheckInAndCheckOutDetailsProps = {
  purchasedProduct: NonNullable<
    NonNullable<OrderByIdQuery["orderById"]>["purchasedProducts"][0]
  >;
};

const CheckInAndCheckOutDetails = ({
  purchasedProduct,
}: CheckInAndCheckOutDetailsProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.PURCHASE_DETAILS",
  });

  const startDate =
    purchasedProduct.inventoryClaims?.[0]?.claimedDateRangeISO?.start ?? "";
  const endDate =
    purchasedProduct.inventoryClaims?.[0]?.claimedDateRangeISO?.end ?? "";

  return (
    <Flex
      gap="0"
      w="full"
      bgColor="ink.100"
      border="2px solid"
      borderColor="border.light"
      borderRadius="100"
    >
      <Flex flexDir="column" gap="25" w="full" p="250">
        <Text visual="smallSemiBold" color="text.light">
          {t("CHECK_IN")}
        </Text>
        <Text visual="bodyMedium" color="text.mid">
          {/* Ensure date is displayed correctly */}
          {formatInTimeZone(startDate, "UTC", "EEE, MMM d")}
          {purchasedProduct.product.accommodationMetadata?.checkIn &&
            ` • ${format(parse(purchasedProduct.product.accommodationMetadata?.checkIn, "HH:mm", new Date()), "h:mma").toLowerCase()}`}
        </Text>
      </Flex>
      <Box
        border="1px solid"
        borderColor="border.light"
        flexShrink={0}
        w="2px"
      />
      <Flex
        flexDir="column"
        justifyContent="flex-end"
        gap="25"
        w="full"
        p="250"
      >
        <Text visual="smallSemiBold" color="text.light" textAlign="right">
          {t("CHECK_OUT")}
        </Text>
        <Text visual="bodyMedium" color="text.mid" textAlign="right">
          {/* Ensure date is displayed correctly */}
          {formatInTimeZone(endDate, "UTC", "EEE, MMM d")}
          {purchasedProduct.product.accommodationMetadata?.checkOut &&
            ` • ${format(parse(purchasedProduct.product.accommodationMetadata?.checkOut, "HH:mm", new Date()), "h:mma").toLowerCase()}`}
        </Text>
      </Flex>
    </Flex>
  );
};

type PurchaseDetailsProps = {
  purchasedProducts: NonNullable<
    OrderByIdQuery["orderById"]
  >["purchasedProducts"];
  guestCount: number;
  productTypeConfig: ProductTypeConfig;
  tierVariant?: string | null;
};

export const PurchaseDetails = ({
  purchasedProducts,
  guestCount,
  productTypeConfig,
  tierVariant = null,
}: PurchaseDetailsProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.PURCHASE_DETAILS",
  });

  if (
    !purchasedProducts ||
    purchasedProducts.length === 0 ||
    !purchasedProducts[0]
  ) {
    return null;
  }

  const getProductDetails = ({
    purchasedProduct,
    guestCount,
    tierVariant: productTierVariant = null,
  }: PurchasedProductProps) => {
    if (purchasedProduct.product.type === EnumProductType.Merch) {
      return (
        <MerchDetails
          key={purchasedProduct._id}
          purchasedProduct={purchasedProduct}
          guestCount={guestCount}
        />
      );
    }
    if (purchasedProduct.product.type === EnumProductType.Event) {
      return (
        <EventDetails
          key={purchasedProduct._id}
          purchasedProduct={purchasedProduct}
          guestCount={guestCount}
        />
      );
    }

    const currentProductTypeConfig = getProductTypeConfig(
      purchasedProduct.product.type,
    );

    switch (currentProductTypeConfig.reservationDetailsCardVariant) {
      case "accommodation":
        return (
          <AccommodationDetails
            key={purchasedProduct._id}
            purchasedProduct={purchasedProduct}
            guestCount={guestCount}
          />
        );
      case "quantity":
        return (
          <QuantityDetails
            key={purchasedProduct._id}
            purchasedProduct={purchasedProduct}
            guestCount={guestCount}
            tierVariant={productTierVariant}
          />
        );
    }
  };

  return (
    <ReservationSection
      icon={productTypeConfig.reservationDetailsIcon}
      title={t(`TITLE_${productTypeConfig.i18nKey}`)}
    >
      {purchasedProducts
        .filter(purchasedProduct => !!purchasedProduct)
        .map((purchasedProduct, index) =>
          getProductDetails({
            purchasedProduct,
            guestCount,
            tierVariant: index === 0 ? tierVariant : null,
          }),
        )}
      {productTypeConfig.hasCheckInOut && (
        <CheckInAndCheckOutDetails purchasedProduct={purchasedProducts[0]} />
      )}
    </ReservationSection>
  );
};
